import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { BiddingBlockApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import { zodResolver } from '@hookform/resolvers/zod'
import { type DefaultValues, type FieldErrors, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  type BoxProps,
  FieldErrorText as EcFieldErrorText,
  FieldLabel as EcFieldLabel,
  FieldRoot,
  Flex,
  HStack,
  Input,
  OutlineDangerButton,
  PrimaryButton,
  TertiaryButton,
  Text,
  VStack,
  chakra,
} from '@enechain/ecloud-designsystem'

import {
  IssuerSelectorView,
  useIssuerOptions,
} from '~/components/bidding/common/IssuerSelector'
import { areaLabelOf } from '~/components/bidding/common/areaLabel'
import {
  AREA_UNSELECTED,
  type IssuerOption,
  isAreaSelectableForIssuer,
  issuerAreaDeliveryContractCode,
  selectedAreaOf,
  settledIssuerOption,
} from '~/components/bidding/common/bidIssuer'
import { initialBidArea } from '~/components/bidding/common/lastBidArea'
import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import type { DsSelectOption } from '~/components/common/DsSelect'
import { FormSelect } from '~/components/common/FormSelect'
import { Modal } from '~/components/common/Modal'
import { useCompanyInfo } from '~/hooks/useCompanyInfo'
import {
  collectFormErrorMessages,
  resolveFormErrorAlertMessage,
} from '~/libs/formErrors'
import type { AreaSideLimit } from '~/models/bidLimits'
import { checkBidLimit, resolveAreaSideLimit } from '~/models/bidLimits'
import { wrapNumberInputChange } from '~/models/numberInputConstraint'
import { BlockLegVolumeSection } from '~/pages/SpotBidding/components/BlockLegVolumeSection'
import { BlockSharedMemoFields } from '~/pages/SpotBidding/components/BlockSharedMemoFields'
import { FeeAutoConsiderHint } from '~/pages/SpotBidding/components/FeeAutoConsiderHint'
import { TimeCodeRangeMiniBar } from '~/pages/SpotBidding/components/TimeCodeRangeMiniBar'
import { useBlockBidLimits } from '~/pages/SpotBidding/hooks/useBlockBidLimits'
import { useSpotBidIssuerForm } from '~/pages/SpotBidding/hooks/useSpotBidIssuerForm'
import { useSpotFeeAutoConsider } from '~/pages/SpotBidding/hooks/useSpotFeeAutoConsider'
import { type SpotFeeAdjustedPriceInput } from '~/pages/SpotBidding/hooks/useSpotFeeAutoConsiderPreview'
import {
  type SpotFeePriceEntriesState,
  useSpotFeePriceEntries,
} from '~/pages/SpotBidding/hooks/useSpotFeePriceEntries'
import type { BlockBidCreateFormInput } from '~/pages/SpotBidding/models/blockBidCreateSchema'
import {
  blockBidCreateSchema,
  blockLegExceedsPerBidLimit,
  desiredPriceBlockBidCreateSchema,
} from '~/pages/SpotBidding/models/blockBidCreateSchema'
import {
  type BlockBidSubmission,
  blockGroupToForm,
} from '~/pages/SpotBidding/models/blockBidWrite'
import {
  buildJepxNote,
  jepxFreeTextMaxLength,
  parseEmissionComment,
} from '~/pages/SpotBidding/models/emissionLinking'
import { spotPriceEntryText } from '~/pages/SpotBidding/models/feePreview'
import { SPOT_KOMA_OPTIONS } from '~/pages/SpotBidding/models/komaOptions'
import { priceInputConstraint } from '~/pages/SpotBidding/models/spotFeeAutoConsider'
import { selectableAreaCodes } from '~/pages/SpotBidding/models/tradableArea'

// chakra v3.29 + React 18 types の既知不具合で FieldLabelProps / FieldErrorTextProps が
// `{ unstyled }` のみに collapse し children 等を受け取れなくなる。 ecloud 由来の component を
// そのまま使い、 取りこぼした props を補う BoxProps で型付けし直す (= IndividualBidModal と同形)。
const FieldLabel = EcFieldLabel as React.FC<BoxProps & { htmlFor?: string }>
const FieldErrorText = EcFieldErrorText as React.FC<BoxProps>

/**
 * コマ別入札量 (配列 field) の最初のエラー文言。
 *
 * `slotVolumes` は配列なので RHF のエラーも要素ごとに入る。 入力欄が 48 個あっても
 * 表下の 1 行に集約して出す (= 項目脇に 48 本並べない)。
 *
 * 走査は共通 helper [[collectFormErrorMessages]] に委ねる (= 配列 / 単一 `FieldError`
 * のどちらの形でも message を拾う)。 helper は `Object.keys` 順に walk するので、
 * 配列エラーでは先頭コマの文言が先に来る。
 */
const firstSlotVolumeErrorMessage = (slotErrors: unknown): string | undefined =>
  collectFormErrorMessages(slotErrors)[0]

/**
 * leg カード / 共通設定カードの見た目。
 *
 * design `bidding/spot.html:393-399` は `.blk-leg` / `.blk-shared-note` /
 * `.blk-meta-card` を **1 つのルールで共有**しており、
 * `border: 1px solid var(--border-low)` / `border-radius: var(--radius-md)` (6px) /
 * `padding: var(--space-3) var(--space-4)` (12px 16px) /
 * `background: var(--surface-secondary)` を当てる。
 * 「LINK / LOOP の 2 leg 横並び時に親-子・A-B の境界が一目で分かるよう」
 * (同 :390 のコメント) というのが design 側の意図で、 2 列にした本 modal では
 * 地と枠が無いと 2 つの leg がどこで切れているのか読めない。
 *
 * border は 3 プロパティに分けて書く。 ショートハンドで色を省くと
 * `currentColor` が効いて黒線になる。
 */
const CARD_STYLE = {
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'Border.lowEmphasis',
  borderRadius: 'md',
  paddingX: 4,
  paddingY: 3,
  background: 'Surface.secondary',
} as const

/** エリアを入れる form の field (両 leg)。 `useSelectableAreas` へ渡す (module 定数で参照を固定)。 */
const PRIMARY_AREA_FIELD_NAMES = ['primaryLeg.areaCode'] as const
const AREA_FIELD_NAMES = [
  'primaryLeg.areaCode',
  'secondaryLeg.areaCode',
] as const

/**
 * 新規作成で開く時のフォームの値。 発注主体区分は画面上部ツールバーの区分で開く。 それが無い
 * (UNSPECIFIED)・選択肢に無い時は、 選択肢が届いた時点で優先順の最上位を既定選択する
 * ([[IssuerSelector]] の `selectsDefault`、 AC-BSP-005d)。
 */
const newBlockFormValues = (
  initialArea: AreaCode | undefined,
  issuerType: LicenseType,
): DefaultValues<BlockBidCreateFormInput> => ({
  kind: 'STANDARD',
  issuerType,
  ...(initialArea !== undefined && { primaryLeg: { areaCode: initialArea } }),
})

/**
 * leg のエリア欄の初期値 (AC-BSP-123)。 `isSelectableArea` (業務エリアの候補 ∩ 取引可能) と、
 * 発注主体区分 `issuerType` (未選択なら既定選択される区分) の両方で選べるエリアだけ入れる。
 */
const initialLegArea = (
  filterArea: AreaCode | undefined,
  isSelectableArea: (area: AreaCode) => boolean,
  issuerOptions: readonly IssuerOption[],
  issuerType: LicenseType,
): AreaCode | undefined =>
  initialBidArea(
    'spot',
    filterArea,
    (code) =>
      isSelectableArea(code) &&
      isAreaSelectableForIssuer(
        settledIssuerOption(issuerOptions, issuerType),
        code,
      ),
  )

/** フォームを作り直す単位。 新規作成か、 編集対象とその保存済みの版 (#10105)。 */
const formKeyOf = (group: BiddingBlockApprovalRequest | undefined): string =>
  group === undefined
    ? 'new'
    : `${group.id}@${group.updatedAt?.seconds ?? ''}.${group.updatedAt?.nanos ?? ''}`

/**
 * Leg の価格欄の値を、 プレビューに問い合わせる 1 行にする。 0.01 刻みの 10 進数でない値 (空欄・指数表記を含む)
 * は undefined で問い合わせに載せない: 1 Leg の不正な値で問い合わせ全体が拒否されると、 もう一方の Leg の
 * 入札価格も出せなくなる (項目エラーは保存時に出す)。 ON の負の希望価格は通す (#11466 Q117 = B。 範囲は算出した
 * 入札価格で判定する)。
 */
const legFeePreviewInput = (
  areaCode: AreaCode | undefined,
  direction: Side | undefined,
  price: number | undefined,
): SpotFeeAdjustedPriceInput | undefined => {
  const text = spotPriceEntryText(price)
  if (
    text === undefined ||
    !/^-?\d+(?:\.\d{1,2})?$/.test(text) ||
    areaCode === undefined ||
    direction === undefined
  ) {
    return undefined
  }
  return { areaCode, direction, desiredPriceYenPerKwh: text }
}

type Props = {
  isOpen: boolean
  /**
   * 業務エリア設定で許可された JEPX 対象エリア (AC-BSP-019 / `~/hooks/useBusinessAreaFilter`)。
   * これ以外のエリアは選択肢に残して選べなくし「業務エリア対象外」 を添える (AC-BSP-005b)。
   */
  areaCodes: readonly AreaCode[]
  /**
   * ページの受渡日。 発注主体区分の選択肢とエリアの選択可否をこの日の広域マスタで求める
   * (保存時の受渡契約コード解決と同じ断面。 #8842)。
   */
  deliveryDate: Date
  /**
   * 取引不可エリア (`ListTradableAreas` の取引可能エリアに無いエリア) のコードセット。
   * undefined = 未取得 (= 制限なし)。 通常入札の札作成モーダル (`IndividualBidModal` /
   * `SpotBidCreateModal`) と同じく選択肢から除く (AC-BSP-005b)。 ブロック側だけ渡さないと、
   * 受渡契約コードを解決できないエリアの STANDARD / LINK / LOOP 札をブロック入札タブ
   * からだけ作成できてしまう。
   */
  disabledAreas?: ReadonlySet<AreaCode>
  /** 作成/更新中 (= 親の Create/Update mutation が pending)。 */
  isSubmitting: boolean
  /** 削除中 (= 親の Delete mutation が pending)。 編集モードのみ意味を持つ。 */
  isDeleting?: boolean
  /** サーバーエラー文言 (= 親が ConnectError から訳した文字列)。 */
  errorMessage: string | undefined
  /** 編集対象 group。 undefined なら新規作成モード。 */
  initialGroup?: BiddingBlockApprovalRequest
  /** ページのエリアフィルタで単一エリアを指定している場合、 その AreaCode (AC-BSP-123)。 */
  defaultAreaCode?: AreaCode
  /**
   * 表示専用 (AC-BSP-001a「編集できるのは下書き (DRAFT) の札のみ。それ以外は
   * 表示専用で開く」。 通常入札の [[IndividualBidModal]] `readOnly` と同じ規約)。
   * 一覧の行クリックは下書き以外の group でも開くが、 提出済・承認済・締切後の
   * group は編集できない。 入力欄をまとめて disabled にし、 footer から保存 /
   * 種別トグル / 削除を外して「閉じる」だけにする。
   *
   * native `fieldset[disabled]` で入力欄をまとめて止める (下記 JSX 参照)。
   * portal で描画される DS `Select` ([[FormSelect]]) と [[IssuerSelector]] は
   * fieldset の cascade が届かないため個別に `isDisabled` を渡す
   * (IndividualBidModal と同じ理由)。
   */
  readOnly?: boolean
  /**
   * 新規作成で開いた時の発注主体区分 (= 画面上部ツールバーで選択中の区分。 AC-BSP-005d)。
   * 未指定・選択肢に無い時は [[IssuerSelector]] が優先順の最上位を既定選択する。
   * 編集はグループの区分を使い、 この値で差し替えない。
   */
  defaultIssuerType?: LicenseType
  onClose: () => void
  /**
   * フォーム入力を確定する。
   * 新規作成モードは親が CreateBiddingBlockApprovalRequest、
   * 編集モードは親が UpdateBiddingBlockApprovalRequest RPC を呼ぶ。
   */
  onSubmit: (form: BlockBidSubmission) => Promise<void>
  /** 削除ボタン押下 (= 編集モードのみ表示)。 親が削除確認 modal を開く。 */
  onDelete?: () => void
}

/**
 * ブロック入札 作成 / 編集 兼用モーダル (design `spot.html` `#block-add-modal`)。
 *
 * 種別 (STANDARD / LINK / LOOP) に応じて 1 または 2 Leg のフォームを表示する。
 * 各 Leg はエリア / 売/買 / 入札量 / 入札価格 / 時間帯 (開始〜終了コマ) の入力欄を持ち、
 * JEPX自由記述欄・メモはブロック単位で 1 つ (両 Leg 共通) の入力欄を持つ。
 * 受渡日は親 page の targetDate を使うためフォームには持たない。
 *
 * `initialGroup` が渡されると編集モードになる: group の leg 一式をフォームに populate し、
 * proto `UpdateBiddingBlockApprovalRequestRequest` に group_type が無い (= 編集で種別変更不可)
 * ため種別セレクターを read-only にする。
 * 編集モードはフッターに削除ボタンを追加で表示する (design `#blk-delete-btn` 相当)。
 */
export const BlockBidCreateModal = ({
  isOpen,
  ...props
}: Props): React.ReactElement | null =>
  // 開くたびにフォームを作り直し、 開いた時の値を useForm の defaultValues で渡す (#10105)。
  isOpen ? (
    <OpenBlockBidCreateModal key={formKeyOf(props.initialGroup)} {...props} />
  ) : null

const OpenBlockBidCreateModal = ({
  areaCodes,
  deliveryDate,
  disabledAreas,
  isSubmitting,
  isDeleting,
  errorMessage,
  initialGroup,
  defaultAreaCode,
  readOnly = false,
  defaultIssuerType = LicenseType.UNSPECIFIED,
  onClose,
  onSubmit,
  onDelete,
}: Omit<Props, 'isOpen'>): React.ReactElement => {
  const { t } = useTranslation()
  const isEdit = initialGroup !== undefined
  // AC-BSP-001a ※: 下書きの編集ではエリアを変えない (変える時はグループを削除して作り直す)。
  // 保存済みの区分が選択肢に無い時は区分だけ選び直させる (AC-BSP-135)。
  const isAreaLocked = readOnly || isEdit
  // 編集で開いたグループは、 グループの発注主体区分をそのまま選択値にする
  // ([[blockGroupToForm]])。 その区分が受渡日の選択肢に無い時は [[IssuerSelector]] が
  // 未選択に戻し、 読み取り専用 (`isLocked`) の編集でもこの時だけは選び直させる。
  const issuerOptions = useIssuerOptions(deliveryDate)

  // 新規作成の leg の初期値に入れられるエリア (編集中の札のエリアを残す指定は新規には無い)。
  const isAreaOptionNow = (area: AreaCode): boolean =>
    selectableAreaCodes(disabledAreas, areaCodes, []).includes(area)

  const [openedValues] = useState<DefaultValues<BlockBidCreateFormInput>>(() =>
    initialGroup !== undefined
      ? blockGroupToForm(initialGroup)
      : newBlockFormValues(
          initialLegArea(
            defaultAreaCode,
            isAreaOptionNow,
            issuerOptions,
            defaultIssuerType,
          ),
          defaultIssuerType,
        ),
  )

  // 開くたびに key 付きで mount し直す ([[OpenBlockBidCreateModal]]) ので、 mount している間は開いている。
  const { feeAutoConsider: openedFee, isSettingsPending } =
    useSpotFeeAutoConsider(true)
  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<BlockBidCreateFormInput>({
    // ON の価格欄は希望価格で、 新規作成・編集を問わず 0.01〜999 の範囲を置かず、 算出した入札価格で
    // 判定する (#11466 Q117 = B / Q117-2。 個別作成と同じ受付条件)。
    resolver: (...args) =>
      zodResolver(
        priceEntries.acceptsDesiredPriceInput
          ? desiredPriceBlockBidCreateSchema
          : blockBidCreateSchema,
      )(...args),
    // secondary leg が非表示になった時にフィールドを登録解除し、
    // STANDARD 種別で submit した場合に secondaryLeg の残データを proto に含めない。
    shouldUnregister: true,
    defaultValues: openedValues,
  })

  const kind = watch('kind')
  const hasSecondaryLeg = kind === 'LINK' || kind === 'LOOP'
  // 編集のエリアは読み取り専用表示で、 FormSelect が登録しない。 shouldUnregister: true
  // でも保存値を watch・検証・送信に残す。 非表示の secondary leg は登録しない。
  if (isEdit && !readOnly) {
    register('primaryLeg.areaCode')
    if (hasSecondaryLeg) register('secondaryLeg.areaCode')
  }
  // 排出係数紐付けも入力欄を持たない (checkbox / 英字は setValue で書く) ので、 同じ理由で register する。
  register('emissionLinked')
  register('emissionAlpha')

  // 会社情報 (登録小売電気事業者 ID)。 判別コメントの組み立てと、 保存済み memo_jepx の分解に使う。
  const companyInfo = useCompanyInfo()
  // 保存済みの memo_jepx は先頭の判別コメントを紐付けの ON と英字に戻し、 残りだけを自由記述欄に出す。
  // 自社 ID と一致する時だけ認識するので、 会社情報が後から届いたら分解し直す。 届くまでの間と同じく、
  // 取得の失敗後に再取得で届いた時も、 利用者が紐付け・英字・自由記述のどれかを触った後は分解し直さない
  // (触った値を保存済みの値で上書きしない)。
  const savedMemoJepx = initialGroup?.primary?.memoJepx
  const emissionEditedRef = useRef(false)
  const markEmissionEdited = (): void => {
    emissionEditedRef.current = true
  }
  useEffect(() => {
    if (!isEdit || emissionEditedRef.current) return
    const emission = parseEmissionComment(savedMemoJepx, companyInfo.retailerId)
    setValue('emissionLinked', emission.linked)
    setValue('emissionAlpha', emission.alpha)
    setValue('memoJepx', emission.freeText)
  }, [isEdit, savedMemoJepx, companyInfo.retailerId, setValue])
  const emissionLinked = watch('emissionLinked') === true
  // 紐付け ON なら判別コメントの分だけ自由記述の上限が縮む (spec §7 機能詳細 4)。
  const jepxFreeMaxLength = jepxFreeTextMaxLength(
    emissionLinked && companyInfo.registered
      ? companyInfo.retailerId
      : undefined,
  )
  // コマ別入札量の表 ([[BlockLegVolumeSection]]) は「選択中のコマ範囲」と
  // 「全コマ一括値」に追従して描画するため、 leg ごとに watch する。
  const primaryTimeCodeFrom = watch('primaryLeg.timeCodeFrom')
  const primaryTimeCodeTo = watch('primaryLeg.timeCodeTo')
  const primaryBaseVolume = watch('primaryLeg.volumeMwh')
  const secondaryTimeCodeFrom = watch('secondaryLeg.timeCodeFrom')
  const secondaryTimeCodeTo = watch('secondaryLeg.timeCodeTo')
  const secondaryBaseVolume = watch('secondaryLeg.volumeMwh')

  // ----- 入札制限 (上限数量) の行内表示 + submit ブロック (AC-BSP-104) -----
  // settings「入札制限」から (エリア × 売買) の上限数量(1札) を引き、 コマ別入札量の表に
  // 渡す。 判定の業務規則は [[slotExceedsQtyMax]] の docstring が SoT。
  //
  // 参照先 matrix の選び方は [[useBlockBidLimits]] に集約している (spec は通常入札と
  // 共通の 1 マトリクスだが、 実装にはブロック専用 matrix が残存しているため)。
  //
  // handleSave が [[blockLegExceedsPerBidLimit]] で同じ述語 (上限数量(1札)、
  // QTY 軸) を submit 直前にも適用し作成・提出をブロックする (AC-BSP-104)。
  // frontend が実際にブロックするのはこの QTY 軸の即時チェックのみ — 下限価格・
  // 上限価格・上限数量(1札) の 3 軸をまとめて検証する SoT は backend 側の
  // TransitionState ゲート (AC-BDL-008 / AC-BSP-104、 block_bid.go
  // ensureWithinBlockBidLimits。 Create/Update 側には同じ QTY 軸のみの
  // ensureWithinBidVolumeLimit が別途ある)。 frontend の QTY チェックは UX 上の
  // 即時フィードバックであって唯一の SoT ではない (backend が二重に検証する)。
  // 上限数量(コマ計) (AC-BSP-080 / bid-limits.md 1-2「コマ計には当該コマのブロック
  // 数量も合算する」) の合算チェックは、 block_bid.go
  // BlockBidUsecase.ensureWithinBidVolumeTotalLimit として server 側にのみ実装済み
  // (T-87)。 この画面では他札 (同一 org の spot 札・他グループの block 札) との合算を
  // 知る手段が無いため、 このコマ別表への行内表示・submit 前の client 側事前ブロックは
  // 引き続き未実装 (超過時は server が FailedPrecondition + typed reason
  // BID_LIMIT_VIOLATION で拒否し、 handleSubmit の catch 側で入札制限違反の文言として
  // 表示される。 超過コマを行単位で示す表示は #8478)。
  const { limits: blockBidLimits } = useBlockBidLimits()
  const primaryAreaCode = selectedAreaOf(watch('primaryLeg.areaCode'))
  const primaryDirection = watch('primaryLeg.direction')
  const secondaryAreaCode = selectedAreaOf(watch('secondaryLeg.areaCode'))
  const secondaryDirection = watch('secondaryLeg.direction')
  // エリア・売買のどちらかが未確定なら制限は引かない (= 判定しない)。
  const primaryLimit =
    primaryAreaCode !== undefined && primaryDirection !== undefined
      ? resolveAreaSideLimit(blockBidLimits, primaryAreaCode, primaryDirection)
      : undefined
  const secondaryLimit =
    secondaryAreaCode !== undefined && secondaryDirection !== undefined
      ? resolveAreaSideLimit(
          blockBidLimits,
          secondaryAreaCode,
          secondaryDirection,
        )
      : undefined
  const primaryQtyMaxMw = primaryLimit?.qtyMax
  const secondaryQtyMaxMw = secondaryLimit?.qtyMax

  // 価格の 上下限価格 違反も同じ制限セルから出す (AC-BSP-052: JEPX 制度上限違反と
  // テナント設定上限違反を区別して表示する。 §7 値域・単位は通常入札・ブロック入札
  // 共通の節)。 JEPX 制度上限 (0.01〜999 円/kWh) 側は zod が別文言で弾く
  // ([[blockBidCreateSchema]])。 数量と同じく**表示のみ**で、 承認申請ゲートは
  // issue #6539 のスコープ。
  const primaryPrice = watch('primaryLeg.priceYenPerKwh')
  const secondaryPrice = watch('secondaryLeg.priceYenPerKwh')
  /** 通常入札 ([[BidLimitHint]]) と同じ判定・同じ文言を leg の価格欄に出す。 */
  const priceLimitMessage = (
    price: number | undefined,
    limit: AreaSideLimit | undefined,
  ): string | undefined => {
    const violations = checkBidLimit(price, undefined, limit)
    if (violations.includes('price-min')) {
      return t('page:biddingCommon.bidLimitHint.priceMinError', {
        min: limit?.priceMin,
      })
    }
    if (violations.includes('price-max')) {
      return t('page:biddingCommon.bidLimitHint.priceMaxError', {
        max: limit?.priceMax,
      })
    }
    return undefined
  }

  const readPrice = useCallback(
    (key: 'primaryLeg' | 'secondaryLeg') => getValues(`${key}.priceYenPerKwh`),
    [getValues],
  )
  const writePrice = useCallback(
    (key: 'primaryLeg' | 'secondaryLeg', price: number) => {
      setValue(`${key}.priceYenPerKwh`, price)
    },
    [setValue],
  )
  const priceEntries: SpotFeePriceEntriesState<'primaryLeg' | 'secondaryLeg'> =
    useSpotFeePriceEntries<'primaryLeg' | 'secondaryLeg'>({
      isOpen: true,
      fee: openedFee,
      isSettingsPending,
      entries: [
        {
          key: 'primaryLeg',
          seed:
            initialGroup?.primary === undefined
              ? undefined
              : {
                  direction: initialGroup.primary.direction,
                  orderPriceYenPerKwh:
                    initialGroup.primary.priceYenPerKwh?.value ?? '0',
                },
          input: legFeePreviewInput(
            primaryAreaCode,
            primaryDirection,
            primaryPrice,
          ),
          isMarket: false,
        },
        ...(hasSecondaryLeg
          ? [
              {
                key: 'secondaryLeg' as const,
                seed:
                  initialGroup?.secondary === undefined
                    ? undefined
                    : {
                        direction: initialGroup.secondary.direction,
                        orderPriceYenPerKwh:
                          initialGroup.secondary.priceYenPerKwh?.value ?? '0',
                      },
                input: legFeePreviewInput(
                  secondaryAreaCode,
                  secondaryDirection,
                  secondaryPrice,
                ),
                isMarket: false,
              },
            ]
          : []),
      ],
      readPrice,
      writePrice,
    })
  const feeAutoConsider = priceEntries.fee
  const primaryFeePreview =
    priceEntries.previews.primaryLeg ??
    ({ kind: 'idle', hint: 'incomplete' } as const)
  const secondaryFeePreview =
    priceEntries.previews.secondaryLeg ??
    ({ kind: 'idle', hint: 'incomplete' } as const)
  const isSettingsUnavailable = !readOnly && isSettingsPending
  const isFeeAutoConsiderBlocking =
    !readOnly && priceEntries.isSubmissionBlocking
  const priceConstraint = priceInputConstraint(feeAutoConsider)
  const isPriceEntryPending = priceEntries.isPriceEntryPending
  // 入札制限の上下限価格は、 ON の間は算出した入札価格に対してプレビューが判定し 2 行文言を出す
  // (AC-BSP-106)。 OFF の間だけ入力値で判定する (AC-BSP-107。 表示のみで、 作成時のブロックは #9002)。
  const primaryPriceLimitMessage =
    feeAutoConsider.kind === 'on'
      ? undefined
      : priceLimitMessage(primaryPrice, primaryLimit)
  const secondaryPriceLimitMessage =
    feeAutoConsider.kind === 'on'
      ? undefined
      : priceLimitMessage(secondaryPrice, secondaryLimit)
  const priceLabelKey =
    feeAutoConsider.kind === 'on'
      ? 'page:biddingBlock.form.desiredPriceYenPerKwh'
      : 'page:biddingBlock.form.priceYenPerKwh'

  // ----- 発注主体区分 -----
  // グループ単位で 1 つ (LINK / LOOP は all-or-nothing で全 leg 同一結果)。 選んだ区分を
  // そのまま `issuerType` (= 書込の issuer_type) として送る。
  //
  // この form は `shouldUnregister: true` なので、 setValue だけで書いた field は
  // submit payload から落ちる。 入力欄が [[IssuerSelector]] (= RHF 非統合) なので
  // 明示的に register して保持させる。
  register('issuerType')
  const watchedIssuerType = watch('issuerType') as LicenseType

  const keepAreaCodes = useMemo(
    () =>
      [
        initialGroup?.primary?.areaCode,
        initialGroup?.secondary?.areaCode,
      ].filter((code): code is AreaCode => code !== undefined),
    [initialGroup],
  )
  const activeAreaFields = hasSecondaryLeg
    ? AREA_FIELD_NAMES
    : PRIMARY_AREA_FIELD_NAMES
  const issuerForm = useSpotBidIssuerForm({
    deliveryDate,
    active: true,
    mode: isEdit ? 'edit' : 'create',
    readOnly,
    savedIssuer: initialGroup?.issuerType,
    issuerField: 'issuerType',
    issuerValue: watchedIssuerType,
    areaFields: activeAreaFields,
    areas: [primaryAreaCode, ...(hasSecondaryLeg ? [secondaryAreaCode] : [])],
    candidateAreaCodes: areaCodes,
    disabledAreas,
    keepAreaCodes,
    unselectedValue: AREA_UNSELECTED,
    getValues,
    setValue,
    setError,
    handleSubmit,
  })
  const {
    selected: selectedIssuer,
    areaSelectOptionsFor,
    reselectBlock,
  } = issuerForm
  const isSavedIssuerFixed = issuerForm.fixed

  // 受渡契約コード (読み取り専用表示、 AC-BSP-030)。 ユーザー入力の対象ではないので RHF に載せない。
  // 保存済みの札で区分・その leg のエリアが保存時のままなら、 保存時に backend が解決したコードを出す
  // (提出済みの札は送信したコードを示す)。 新規作成と、 区分を選び直した下書き (AC-BSP-135) は、
  // 選択中の区分 × エリアで backend が解決済みのコード ([[issuerAreaDeliveryContractCode]]、
  // 発注主体の欄のエリア併記と同じ値) を出す。 保存すると backend が同じ区分・エリアで解き直す。
  const deliveryContractCodeOf = (
    area: AreaCode | undefined,
    savedLeg: { areaCode: AreaCode; deliveryContractCode?: string } | undefined,
  ): string | undefined =>
    initialGroup !== undefined &&
    watchedIssuerType === initialGroup.issuerType &&
    savedLeg !== undefined &&
    area === savedLeg.areaCode
      ? savedLeg.deliveryContractCode
      : issuerAreaDeliveryContractCode(selectedIssuer, area)
  const primaryDeliveryContractCode = deliveryContractCodeOf(
    primaryAreaCode,
    initialGroup?.primary,
  )
  const secondaryDeliveryContractCode = deliveryContractCodeOf(
    secondaryAreaCode,
    initialGroup?.secondary,
  )

  // AC-3304-1/2: 価格・数量入力欄は JEPX 範囲外・不正な小数点桁の入力を受け付けない
  // (= constrainNumberInput でラップした onChange。 register を変数化するのは
  // JSX 上で {...register(...)} の後に onChange を上書きしつつ元の onChange も
  // 呼び出す必要があるため)。
  const primaryVolumeReg = register('primaryLeg.volumeMwh', {
    valueAsNumber: true,
  })
  const primaryPriceReg = register('primaryLeg.priceYenPerKwh', {
    valueAsNumber: true,
  })
  // secondaryLeg は STANDARD 種別のとき非表示 + `shouldUnregister: true` で
  // 未登録に保つ必要があるため、 primaryLeg と違い top-level で register を
  // 変数化しない (= 無条件に register すると STANDARD でも secondaryLeg が
  // 必須フィールドとして登録され続け、 update 時に誤って validation エラーに
  // なる regression を起こす)。 secondaryLeg の入力欄は [[hasSecondaryLeg]] の
  // JSX ブロック内で直接 register する (= 下記 JSX 参照)。

  // 2 つ目の leg のエリアの初期値は、 通常ブロックから切り替えた時にだけ値と欄の既定値の両方へ入れる (AC-BSP-123、 #10105)。
  const [secondaryLegDefaultArea, setSecondaryLegDefaultArea] =
    useState<AreaCode>(AREA_UNSELECTED)
  const handleKindChange = (next: BlockBidCreateFormInput['kind']): void => {
    if (isEdit) return
    if (kind === 'STANDARD' && next !== 'STANDARD') {
      const area =
        initialLegArea(
          defaultAreaCode,
          isAreaOptionNow,
          issuerOptions,
          getValues('issuerType') as LicenseType,
        ) ?? AREA_UNSELECTED
      setSecondaryLegDefaultArea(area)
      setValue('secondaryLeg.areaCode', area)
    }
    setValue('kind', next)
  }

  // 選択肢は通常入札の札作成モーダルと同じ [[useSelectableAreas]] で組む (= 業務
  // エリアの候補 ∩ 取引可能 + 編集中の leg エリアの keep 例外)。 LINK / LOOP は
  // 1 group で 2 leg のエリアを編集するため keep は 2 件渡し、 選択解除も両 leg が対象。
  const handleSave = (): void => {
    // 二重防御 (作成・更新ボタンの disabled と同一条件)。 PrimaryButton は aria-disabled の
    // soft-disabled で onClick が発火しうるため、 入札制限を読めない間は送信まで進めない。
    if (isFeeAutoConsiderBlocking) return
    issuerForm.submit(
      async (form) => {
        // AC-BSP-104: 対象コマが上限数量(1札) を超過するブロック入札は作成・提出を
        // ブロックする。 [[BlockLegVolumeSection]] の行エラー表示と同じ per-slot 述語
        // ([[slotExceedsQtyMax]]) を submit 直前にも適用する。
        if (blockLegExceedsPerBidLimit(form.primaryLeg, primaryQtyMaxMw)) {
          setError('primaryLeg.volumeMwh', {
            message: t('page:biddingBlock.form.volumeOverQtyLimit', {
              qtyMaxMw: primaryQtyMaxMw?.toFixed(1),
            }),
          })
          return
        }
        if (
          form.secondaryLeg !== undefined &&
          blockLegExceedsPerBidLimit(form.secondaryLeg, secondaryQtyMaxMw)
        ) {
          setError('secondaryLeg.volumeMwh', {
            message: t('page:biddingBlock.form.volumeOverQtyLimit', {
              qtyMaxMw: secondaryQtyMaxMw?.toFixed(1),
            }),
          })
          return
        }
        // 判別コメント (紐付け ON の時) と自由記述を連結し、 両 Leg の memo_jepx に載せる (AC-BSP-083 / 086)。
        // 利用者が触っていなければ保存済みの文字列をそのまま送る ([[buildJepxNote]])。
        const memoJepx = buildJepxNote({
          linkedRetailerId:
            form.emissionLinked === true && companyInfo.registered
              ? companyInfo.retailerId
              : undefined,
          alpha: form.emissionAlpha ?? '',
          freeText: form.memoJepx ?? '',
          saved: isEdit
            ? { memoJepx: savedMemoJepx, retailerId: companyInfo.retailerId }
            : undefined,
        })
        if (!memoJepx.ok) {
          setError('memoJepx', {
            message: t('page:biddingSpot.form.memoJepxCombinedLengthError', {
              max: memoJepx.freeTextMaxLength,
            }),
          })
          return
        }
        // 手数料自動考慮 (AC-BSP-004b / AC-BSP-106): Leg ごとにプレビューで見ていた状態を付けて希望価格を送り
        // (OFF でも付ける)、 OFF として扱う間は入力値を入札価格として送る。 算出した入札価格が範囲を外れる
        // Leg が 1 つでもあれば、 ブロック全体 (ペアは両 Leg) を送らない。
        const submission = priceEntries.resolveSubmission(t, [
          {
            key: 'primaryLeg',
            price: form.primaryLeg.priceYenPerKwh,
            isMarket: false,
          },
          ...(form.secondaryLeg === undefined
            ? []
            : [
                {
                  key: 'secondaryLeg' as const,
                  price: form.secondaryLeg.priceYenPerKwh,
                  isMarket: false,
                },
              ]),
        ])
        if (submission.kind === 'error') {
          setError(`${submission.key}.priceYenPerKwh`, {
            message: submission.message,
          })
          return
        }
        if (submission.kind === 'hold') return
        await onSubmit({
          ...form,
          memoJepx: memoJepx.note,
          feeAutoConsider: submission.confirmed,
        })
      },
      (formErrors: FieldErrors<BlockBidCreateFormInput>) => {
        const alertMessage = resolveFormErrorAlertMessage(
          formErrors,
          t('page:biddingBlock.serverError.validation'),
        )
        if (alertMessage !== undefined) {
          setError('root.serverError', { message: alertMessage })
        } else {
          // 直前の失敗で出した alert が残らないよう明示的に消す
          // (項目脇の表示だけに切り替わるため)。
          clearErrors('root.serverError')
        }
      },
    )
  }

  const serverError =
    errorMessage !== undefined
      ? errorMessage
      : errors.root?.serverError?.message
  // レグの項目のエラーは改行で連なって届く (`resolveFormErrorAlertMessage`)。 1 件目を Alert の
  // 見出し、 残りを本文の行にする。
  const [serverErrorTitle, ...serverErrorRest] = serverError?.split('\n') ?? []

  // 広域マスタ未登録のエリアは選択肢に出さない (AC-BSP-005b)。 選択中のエリアは leg ごとに残す。
  // 区分を選び直せば直る理由は発注主体の欄に出す。 どの区分でも直らない「作り直し」 は欄に
  // 帰着しないので、 モーダル上部の Alert に出す (design action-reason.md の区分 1)。
  const reselectFieldBlock =
    reselectBlock === 'deleteAndRecreate' ? undefined : reselectBlock
  const areaOptionsFor = (current: AreaCode | undefined): DsSelectOption[] =>
    areaSelectOptionsFor(selectedIssuer, current).map(
      ({ area, label, disabled }) => ({
        value: String(area),
        label,
        disabled,
      }),
    )
  /** 売買。 native `<option>` を並べていたのを DS `Select` のデータ形に揃える。 */
  const directionOptions = [
    { value: String(Side.BUY), label: t('page:biddingBlock.form.buy') },
    { value: String(Side.SELL), label: t('page:biddingBlock.form.sell') },
  ]

  /**
   * コマ select の選択肢。 **番号だけでなく時間帯も出す** (AC-BSP-004c、issue #7464)。
   *
   * 以前は「1コマ」「2コマ」… と**番号だけ**で、何時から何時かが選択肢から読めなかった。
   * spec AC-BSP-004c は「コマ番号と時間帯の両方を 1 つの選択肢文字列に含める
   * (**番号のみの数値入力欄は使わない**)」 と定めており、 同じ画面の通常入札
   * ([[IndividualBidModal]]) は既に「28 (13:30〜14:00)」 形式になっていた。
   * **入札画面の中で表記が割れていた**ので、通常入札と同じ `SPOT_KOMA_OPTIONS` を使う。
   *
   * 表記を独自に作らず共有モデルを引くことで、 形式の決定 (prefix 無し・ゼロ埋め無し。
   * issue #5365 Des 裁定 2026-08-18) が 1 箇所にしか無い状態を保つ。
   */

  /**
   * leg の見出し。 design `bidding/spot.html:5754-5762` の `BLK_LEG_PRESETS` は
   * **種別ごとに別ラベル**を持つ (通常ブロック / リンクブロック (親)(子) /
   * ループブロック (A)(B))。 実装は種別に関わらず英字固定の
   * 「Primary Leg / Secondary Leg」 で、 **どちらが親でどちらが子か**を見出しから
   * 読めなかった (#7636 の 8080 突合)。
   *
   * LINK の親子・LOOP の A/B は約定条件そのものが違う (親が約定した時だけ子が
   * 約定対象、 A と B は双方約定か双方不成立) ため、 見出しで区別が付かないと 2 つの leg の
   * どちらを編集しているか取り違える。
   */
  const legLabel = (isPrimary: boolean): string => {
    if (kind === 'LINK') {
      return t(
        isPrimary
          ? 'page:biddingBlock.form.legLabelLINK_P'
          : 'page:biddingBlock.form.legLabelLINK_C',
      )
    }
    if (kind === 'LOOP') {
      return t(
        isPrimary
          ? 'page:biddingBlock.form.legLabelLOOP_A'
          : 'page:biddingBlock.form.legLabelLOOP_B',
      )
    }
    return t('page:biddingBlock.form.legLabelSTANDARD')
  }

  return (
    <Modal
      footer={
        readOnly ? (
          // 表示専用は「閉じる」だけ (IndividualBidModal と同形。 保存できない
          // 画面に保存ボタンを残さない)。
          <Flex justifyContent="flex-end" width="100%">
            <TertiaryButton onClick={onClose} type="button">
              {t('common:action.close')}
            </TertiaryButton>
          </Flex>
        ) : (
          <Flex alignItems="center" justifyContent="space-between" width="100%">
            <Box>
              {/* 削除ボタン (= 編集モードのみ、 design `#blk-delete-btn` 相当)。
                  押下は親に委譲する (= 親が削除確認 modal を開いてから Delete RPC を呼ぶ)。 */}
              {isEdit && onDelete !== undefined && (
                <OutlineDangerButton
                  disabled={isSubmitting || isDeleting === true}
                  loading={isDeleting}
                  onClick={onDelete}
                  type="button"
                >
                  {t('page:biddingBlock.editModal.deleteLabel')}
                </OutlineDangerButton>
              )}
            </Box>
            <HStack gap={2}>
              <TertiaryButton
                disabled={isSubmitting}
                onClick={onClose}
                type="button"
              >
                {t('page:biddingBlock.createModal.cancelLabel')}
              </TertiaryButton>
              <PrimaryButton
                disabled={
                  isSubmitting ||
                  isFeeAutoConsiderBlocking ||
                  reselectBlock !== undefined
                }
                loading={isSubmitting}
                onClick={handleSave}
                type="button"
              >
                {isEdit
                  ? t('page:biddingBlock.editModal.submitLabel')
                  : t('page:biddingBlock.createModal.submitLabel')}
              </PrimaryButton>
            </HStack>
          </Flex>
        )
      }
      isOpen
      onClose={onClose}
      scrollBehavior="inside"
      size="xl"
      title={
        readOnly
          ? t('page:biddingBlock.editModal.viewTitle')
          : isEdit
            ? t('page:biddingBlock.editModal.title')
            : t('page:biddingBlock.createModal.title')
      }
    >
      {/* 表示専用は native `fieldset[disabled]` で中の入力をまとめて止める
          (= IndividualBidModal と同形)。 portal 描画の DS Select / IssuerSelector は
          cascade が届かないため個別に `isDisabled` を渡す (下記各 field 参照)。 */}
      <chakra.fieldset margin={0} padding={0} border="none" disabled={readOnly}>
        <VStack align="stretch" gap={4}>
          {reselectBlock === 'deleteAndRecreate' && (
            <Alert
              status="error"
              title={t(
                'page:biddingSpot.form.draftIssuerReselect.deleteAndRecreate',
              )}
            />
          )}
          {serverError !== undefined && (
            <Alert
              description={
                serverErrorRest.length > 0
                  ? serverErrorRest.map((line) => (
                      <Text key={line}>{line}</Text>
                    ))
                  : undefined
              }
              status="error"
              title={serverErrorTitle}
            />
          )}

          {/* ===== 種別選択 (STANDARD / LINK / LOOP) ===== */}
          <VStack align="stretch" gap={2}>
            {/*
            'kind' はどの native input にも register していない「Button 駆動のみ」の
            仮想フィールドだったため、 useForm の `shouldUnregister: true` により
            handleSubmit 時に値が消失し (= 未登録フィールドは reset() 後も追跡対象に
            残らない)、 zod の enum 検証が "Invalid option" で落ちて secondaryLeg も
            誤って必須表示される事故があった。 hidden input で register し、 常に
            追跡対象に含めることで解消する (= 表示は Button group のまま)。
          */}
            <input type="hidden" {...register('kind')} />
            <Text fontWeight="semibold">
              {t('page:biddingBlock.form.kindLabel')}
            </Text>
            {/* 種別 (通常 / リンク / ループ) は 1 つを選ぶ切替なので DS SegmentedControl
                (8080 `#blk-kind-segmented` = `.ecl-segmented`、 UI 辞書
                `components/segmented.md`)。 編集時は種別を変えられないので全項目を無効。 */}
            <Box alignSelf="flex-start">
              <AccessibleSegmentedControl
                aria-label={t('page:biddingBlock.form.kindLabel')}
                name="blockBidCreate-kind"
                onSelect={handleKindChange}
                options={(['STANDARD', 'LINK', 'LOOP'] as const).map((k) => ({
                  value: k,
                  label: t(`page:biddingBlock.kind.${k}`),
                  disabled: isEdit,
                }))}
                selected={kind}
              />
            </Box>
          </VStack>

          {/* 発注主体区分。 グループ全 leg に同一適用する
            (design `spot.html` `#blk-issuer-selector`)。 以前はこの欄が無く、
            ブロック入札はどの名義で出したか記録されていなかった (issue #5366)。 */}
          {/* 発注主体はブロック共通の属性なので、 design の共通設定カード
            (`.blk-meta-card`。 leg カードと同じルールを共有) と同じ見た目で囲む。 */}
          <Box {...CARD_STYLE}>
            {/* 既存の下書きを開いて編集する時は読み取り専用 (AC-BSP-001a ※)。 入札一覧は
              ツールバーの区分でスコープされるので、 編集で区分を変えて保存するとグループが
              一覧から消える。 変えるなら削除して作り直す。 保存済みの区分が受渡日の選択肢に
              無い時だけは、 保存できるよう選び直させる (AC-BSP-135)。 */}
            <IssuerSelectorView
              errorMessage={
                errors.issuerType?.message ??
                (reselectFieldBlock !== undefined
                  ? t(
                      `page:biddingSpot.form.draftIssuerReselect.${reselectFieldBlock}`,
                    )
                  : undefined)
              }
              isDisabled={readOnly}
              isLocked={isEdit && isSavedIssuerFixed}
              onChange={issuerForm.onChange}
              options={issuerForm.options}
              value={watchedIssuerType}
            />
          </Box>

          {/*
          LINK / LOOP の 2 leg は **2 列横並び**にする (spec `spot-bidding.md` の
          「LINK: 親Leg (LINK-P) と子Leg (LINK-C) を 2 列横並びで表示し、 比較しやすくする」、
          「LOOP: LOOP-A と LOOP-B を 2 列横並びで表示する」)。 design `bidding/spot.html:6265-6267` も
          `isWide = (kind === 'LINK' || kind === 'LOOP')` で
          `gridTemplateColumns` を 2 列へ切り替えている。

          縦に連結していた頃は、 Secondary Leg に到達するのに Primary Leg 全体
          (最大 48 行のコマ別入札量表を含む) を縦スクロールする必要があり、
          2 leg のエリア・時間帯・数量を目視で突き合わせられなかった (#7770)。

          `minmax(0, 1fr)` にするのは、 中のコマ別表が広い時に列が grid の
          既定 (`auto` = min-content) で押し広げられて横スクロールするのを防ぐため。
        */}
          <Box
            // 2 leg の並びはレイアウトそのものが spec の契約 (:729/:730「2 列横並びで
            // 表示し、 比較しやすくする」)。
            gap={4}
            gridTemplateColumns={
              hasSecondaryLeg ? 'repeat(2, minmax(0, 1fr))' : '1fr'
            }
            display="grid"
          >
            {/* ===== Primary Leg ===== */}
            <VStack align="stretch" gap={3} {...CARD_STYLE}>
              <Text textStyle="blockLegTitle" color="Text.highEmphasis">
                {legLabel(true)}
              </Text>
              <Box gap={3} gridTemplateColumns="1fr 1fr" display="grid">
                <FieldRoot
                  invalid={errors.primaryLeg?.areaCode !== undefined}
                  required
                >
                  <FieldLabel>
                    {t('page:biddingBlock.form.areaCode')}
                  </FieldLabel>
                  {isAreaLocked && !readOnly ? (
                    <Input
                      aria-label={t('page:biddingBlock.form.areaCode')}
                      readOnly
                      value={
                        primaryAreaCode === undefined
                          ? ''
                          : areaLabelOf(primaryAreaCode)
                      }
                    />
                  ) : (
                    <FormSelect
                      width="100%"
                      control={control}
                      isDisabled={isAreaLocked}
                      name="primaryLeg.areaCode"
                      numeric
                      options={areaOptionsFor(primaryAreaCode)}
                      placeholder={t('common:form.selectPlaceholder')}
                    />
                  )}
                  <FieldErrorText>
                    {errors.primaryLeg?.areaCode?.message}
                  </FieldErrorText>
                </FieldRoot>

                <FieldRoot
                  invalid={errors.primaryLeg?.direction !== undefined}
                  required
                >
                  <FieldLabel>
                    {t('page:biddingBlock.form.direction')}
                  </FieldLabel>
                  <FormSelect
                    width="100%"
                    control={control}
                    isDisabled={readOnly}
                    name="primaryLeg.direction"
                    numeric
                    options={directionOptions}
                    placeholder={t('common:form.selectPlaceholder')}
                  />
                  <FieldErrorText>
                    {errors.primaryLeg?.direction?.message}
                  </FieldErrorText>
                </FieldRoot>

                <FieldRoot
                  invalid={errors.primaryLeg?.timeCodeFrom !== undefined}
                  required
                >
                  <FieldLabel>
                    {t('page:biddingBlock.form.timeCodeFrom')}
                  </FieldLabel>
                  <FormSelect
                    width="100%"
                    control={control}
                    isDisabled={readOnly}
                    name="primaryLeg.timeCodeFrom"
                    numeric
                    options={SPOT_KOMA_OPTIONS}
                    placeholder={t('common:form.selectPlaceholder')}
                  />
                  <FieldErrorText>
                    {errors.primaryLeg?.timeCodeFrom?.message}
                  </FieldErrorText>
                </FieldRoot>

                <FieldRoot
                  invalid={errors.primaryLeg?.timeCodeTo !== undefined}
                  required
                >
                  <FieldLabel>
                    {t('page:biddingBlock.form.timeCodeTo')}
                  </FieldLabel>
                  <FormSelect
                    width="100%"
                    control={control}
                    isDisabled={readOnly}
                    name="primaryLeg.timeCodeTo"
                    numeric
                    options={SPOT_KOMA_OPTIONS}
                    placeholder={t('common:form.selectPlaceholder')}
                  />
                  <FieldErrorText>
                    {errors.primaryLeg?.timeCodeTo?.message}
                  </FieldErrorText>
                </FieldRoot>

                <FieldRoot
                  alignItems="stretch"
                  width="100%"
                  invalid={errors.primaryLeg?.priceYenPerKwh !== undefined}
                  required
                >
                  <FieldLabel>{t(priceLabelKey)}</FieldLabel>
                  <Input
                    css={{ width: '100%' }}
                    disabled={isPriceEntryPending}
                    max={priceConstraint.max}
                    min={priceConstraint.min}
                    step={priceConstraint.step}
                    type="number"
                    {...primaryPriceReg}
                    onChange={wrapNumberInputChange(
                      primaryPriceReg,
                      priceConstraint,
                    )}
                  />
                  <FieldErrorText>
                    {errors.primaryLeg?.priceYenPerKwh?.message}
                  </FieldErrorText>
                  {/* 入札制限 (上下限価格) 違反。 zod の JEPX 制度上限エラーとは
                  別文言・別行で出す (AC-BSP-052)。 表示のみで submit は
                  ブロックしない (= 承認申請ゲートは issue #6539)。 */}
                  {primaryPriceLimitMessage !== undefined && (
                    <Text marginTop={1} color="Text.negativeHigh" fontSize="sm">
                      {primaryPriceLimitMessage}
                    </Text>
                  )}
                  {(!readOnly || !isPriceEntryPending) && (
                    <FeeAutoConsiderHint
                      fee={feeAutoConsider}
                      isSettingsUnavailable={isSettingsUnavailable}
                      preview={primaryFeePreview}
                    />
                  )}
                </FieldRoot>

                {/* 受渡契約コード = 発注主体区分 × エリア × 受渡日から広域マスタを解決した
                  読み取り専用値 (AC-BSP-030、 決定の経緯「ブロック作成モーダルの
                  受渡契約コード入力方式」#6222: ユーザーに選ばせる入力欄は設けない)。
                  作成・更新時に backend が決めて保存した値で、 保存前の旧い leg は空のため
                  「—」 を表示する。 参照表示なので input ではなく素のテキストで出す
                  (design/CLAUDE.md 「参照表示」 規約)。 */}
                <FieldRoot>
                  <FieldLabel>
                    {t('page:biddingBlock.form.deliveryContractCode')}
                  </FieldLabel>
                  <Text
                    color="Text.highEmphasis"
                    data-testid="primaryLeg-deliveryContractCode"
                  >
                    {primaryDeliveryContractCode !== undefined &&
                    primaryDeliveryContractCode !== ''
                      ? primaryDeliveryContractCode
                      : t('page:biddingBlock.form.deliveryContractCodeEmpty')}
                  </Text>
                </FieldRoot>
              </Box>

              {/* コマ範囲のミニバー + 「Nコマ / M時間」 表示 (AC-BSP-030、 機能詳細
                「コマ範囲: 開始〜終了のセレクタで指定し、 48セルのミニバーで選択範囲を
                視覚化する。 選択中のコマ数と時間数（「Nコマ / M時間」）を表示する」)。
                開始/終了コマの select 直下、 入札量入力より上に置く。 */}
              <TimeCodeRangeMiniBar
                timeCodeFrom={primaryTimeCodeFrom}
                timeCodeTo={primaryTimeCodeTo}
              />

              {/* 入札量 = 常時コマ別表 + 全コマ一括 (AC-BSP-030a、 2026-08-18 Des 裁定)。
              コマ範囲の入力欄より下に置き、 範囲を選ぶと表がその範囲に追従する。 */}
              <BlockLegVolumeSection
                baseErrorMessage={errors.primaryLeg?.volumeMwh?.message}
                baseVolumeMwh={primaryBaseVolume}
                baseVolumeRegistration={primaryVolumeReg}
                control={control}
                legPrefix="primaryLeg"
                qtyMaxMw={primaryQtyMaxMw}
                slotErrorMessage={firstSlotVolumeErrorMessage(
                  errors.primaryLeg?.slotVolumes,
                )}
                timeCodeFrom={primaryTimeCodeFrom}
                timeCodeTo={primaryTimeCodeTo}
              />
            </VStack>

            {/* ===== Secondary Leg (LINK / LOOP のみ) ===== */}
            {hasSecondaryLeg && (
              <VStack align="stretch" gap={3} {...CARD_STYLE}>
                <Text textStyle="blockLegTitle" color="Text.highEmphasis">
                  {legLabel(false)}
                </Text>
                <Box gap={3} gridTemplateColumns="1fr 1fr" display="grid">
                  <FieldRoot
                    invalid={errors.secondaryLeg?.areaCode !== undefined}
                    required
                  >
                    <FieldLabel>
                      {t('page:biddingBlock.form.areaCode')}
                    </FieldLabel>
                    {isAreaLocked && !readOnly ? (
                      <Input
                        aria-label={t('page:biddingBlock.form.areaCode')}
                        readOnly
                        value={
                          secondaryAreaCode === undefined
                            ? ''
                            : areaLabelOf(secondaryAreaCode)
                        }
                      />
                    ) : (
                      <FormSelect
                        width="100%"
                        control={control}
                        defaultValue={secondaryLegDefaultArea}
                        isDisabled={isAreaLocked}
                        name="secondaryLeg.areaCode"
                        numeric
                        options={areaOptionsFor(secondaryAreaCode)}
                        placeholder={t('common:form.selectPlaceholder')}
                      />
                    )}
                    <FieldErrorText>
                      {errors.secondaryLeg?.areaCode?.message}
                    </FieldErrorText>
                  </FieldRoot>

                  <FieldRoot
                    invalid={errors.secondaryLeg?.direction !== undefined}
                    required
                  >
                    <FieldLabel>
                      {t('page:biddingBlock.form.direction')}
                    </FieldLabel>
                    <FormSelect
                      width="100%"
                      control={control}
                      isDisabled={readOnly}
                      name="secondaryLeg.direction"
                      numeric
                      options={directionOptions}
                      placeholder={t('common:form.selectPlaceholder')}
                    />
                    <FieldErrorText>
                      {errors.secondaryLeg?.direction?.message}
                    </FieldErrorText>
                  </FieldRoot>

                  <FieldRoot
                    invalid={errors.secondaryLeg?.timeCodeFrom !== undefined}
                    required
                  >
                    <FieldLabel>
                      {t('page:biddingBlock.form.timeCodeFrom')}
                    </FieldLabel>
                    <FormSelect
                      width="100%"
                      control={control}
                      isDisabled={readOnly}
                      name="secondaryLeg.timeCodeFrom"
                      numeric
                      options={SPOT_KOMA_OPTIONS}
                      placeholder={t('common:form.selectPlaceholder')}
                    />
                    <FieldErrorText>
                      {errors.secondaryLeg?.timeCodeFrom?.message}
                    </FieldErrorText>
                  </FieldRoot>

                  <FieldRoot
                    invalid={errors.secondaryLeg?.timeCodeTo !== undefined}
                    required
                  >
                    <FieldLabel>
                      {t('page:biddingBlock.form.timeCodeTo')}
                    </FieldLabel>
                    <FormSelect
                      width="100%"
                      control={control}
                      isDisabled={readOnly}
                      name="secondaryLeg.timeCodeTo"
                      numeric
                      options={SPOT_KOMA_OPTIONS}
                      placeholder={t('common:form.selectPlaceholder')}
                    />
                    <FieldErrorText>
                      {errors.secondaryLeg?.timeCodeTo?.message}
                    </FieldErrorText>
                  </FieldRoot>

                  <FieldRoot
                    alignItems="stretch"
                    width="100%"
                    invalid={errors.secondaryLeg?.priceYenPerKwh !== undefined}
                    required
                  >
                    <FieldLabel>{t(priceLabelKey)}</FieldLabel>
                    {(() => {
                      const reg = register('secondaryLeg.priceYenPerKwh', {
                        valueAsNumber: true,
                      })
                      return (
                        <Input
                          css={{ width: '100%' }}
                          disabled={isPriceEntryPending}
                          max={priceConstraint.max}
                          min={priceConstraint.min}
                          step={priceConstraint.step}
                          type="number"
                          {...reg}
                          onChange={wrapNumberInputChange(reg, priceConstraint)}
                        />
                      )
                    })()}
                    <FieldErrorText>
                      {errors.secondaryLeg?.priceYenPerKwh?.message}
                    </FieldErrorText>
                    {secondaryPriceLimitMessage !== undefined && (
                      <Text
                        marginTop={1}
                        color="Text.negativeHigh"
                        fontSize="sm"
                      >
                        {secondaryPriceLimitMessage}
                      </Text>
                    )}
                    {(!readOnly || !isPriceEntryPending) && (
                      <FeeAutoConsiderHint
                        fee={feeAutoConsider}
                        isSettingsUnavailable={isSettingsUnavailable}
                        preview={secondaryFeePreview}
                      />
                    )}
                  </FieldRoot>

                  {/* 受渡契約コード (= primaryLeg と同じ規約。 backend が保存した読み取り専用値)。 */}
                  <FieldRoot>
                    <FieldLabel>
                      {t('page:biddingBlock.form.deliveryContractCode')}
                    </FieldLabel>
                    <Text
                      color="Text.highEmphasis"
                      data-testid="secondaryLeg-deliveryContractCode"
                    >
                      {secondaryDeliveryContractCode !== undefined &&
                      secondaryDeliveryContractCode !== ''
                        ? secondaryDeliveryContractCode
                        : t('page:biddingBlock.form.deliveryContractCodeEmpty')}
                    </Text>
                  </FieldRoot>
                </Box>

                {/* コマ範囲のミニバー + 「Nコマ / M時間」 表示 (primaryLeg と同じ規約)。 */}
                <TimeCodeRangeMiniBar
                  timeCodeFrom={secondaryTimeCodeFrom}
                  timeCodeTo={secondaryTimeCodeTo}
                />

                {/* secondaryLeg の入札量も primaryLeg と同じコマ別表で入力する
                (AC-BSP-031/032: LINK / LOOP の各 Leg は AC-BSP-030a と同様の入力方式)。
                register は primaryLeg と違いこのブロック内で行う (= STANDARD 種別では
                未登録に保つ。 上の register 変数化の注記を参照)。 */}
                {(() => {
                  const secondaryVolumeReg = register(
                    'secondaryLeg.volumeMwh',
                    {
                      valueAsNumber: true,
                    },
                  )
                  return (
                    <BlockLegVolumeSection
                      baseErrorMessage={errors.secondaryLeg?.volumeMwh?.message}
                      baseVolumeMwh={secondaryBaseVolume}
                      baseVolumeRegistration={secondaryVolumeReg}
                      control={control}
                      legPrefix="secondaryLeg"
                      qtyMaxMw={secondaryQtyMaxMw}
                      slotErrorMessage={firstSlotVolumeErrorMessage(
                        errors.secondaryLeg?.slotVolumes,
                      )}
                      timeCodeFrom={secondaryTimeCodeFrom}
                      timeCodeTo={secondaryTimeCodeTo}
                    />
                  )
                })()}
              </VStack>
            )}
          </Box>

          {/* JEPX自由記述欄・メモはブロック単位で 1 つ (LINK の親子・LOOP の A/B で共通、
              AC-BSP-031/032)。 design `spot.html` の `.blk-shared-note` と同じく leg
              カードの外に共通設定カードとして 1 つだけ置き、 保存時に両 leg へ同じ値を
              載せる ([[formToCreateBlockInput]])。 */}
          <VStack align="stretch" gap={3} {...CARD_STYLE}>
            <BlockSharedMemoFields
              control={control}
              emission={{
                registered: companyInfo.registered,
                retailerId: companyInfo.retailerId,
                fetchFailed: companyInfo.isError,
                isLoading: companyInfo.isLoading,
                linked: emissionLinked,
                alpha: watch('emissionAlpha') ?? '',
                alphaError: errors.emissionAlpha?.message,
                onLinkedChange: (linked) => {
                  markEmissionEdited()
                  setValue('emissionLinked', linked)
                },
                onAlphaChange: (alpha) => {
                  markEmissionEdited()
                  setValue('emissionAlpha', alpha, { shouldValidate: true })
                },
              }}
              errors={errors}
              jepxFreeDisabled={isEdit && companyInfo.isLoading}
              jepxFreeMaxLength={jepxFreeMaxLength}
              jepxFreeRegistration={register('memoJepx', {
                onChange: markEmissionEdited,
              })}
              kind={kind}
              register={register}
            />
          </VStack>
        </VStack>
      </chakra.fieldset>
    </Modal>
  )
}
